import type { AttachmentFlow } from 'components/attachments/types';
import type { AttachmentFile } from 'types/attachmentTypes';
import { create } from 'zustand';

/**
 * Attachments the user is currently working with (sales order, waiting list).
 *
 * `attachments` is the working list for whichever context is on top of the
 * attachment context stack; `currentAttachmentContext` is that context's
 * orderUid, `attachmentFlow` the callbacks used to upload/download/delete for
 * it, and `attachmentReadOnly` whether the screen may still change it. Those
 * three always move together — see `setAttachmentContext`.
 *
 * `selectedAttachment` is the file open in the preview screen and
 * `attachmentToRemove` the uploadUid awaiting removal confirmation.
 *
 * Not persisted: attachments are re-fetched per order and a half-finished
 * upload must not survive a relaunch.
 */
type AttachmentState = {
  attachments: AttachmentFile[];
  selectedAttachment: AttachmentFile | null;
  currentAttachmentContext: string | null;
  attachmentFlow: AttachmentFlow | null;
  attachmentReadOnly: boolean;
  attachmentToRemove: string | null;
};

type AttachmentActions = {
  setAttachments: (attachments: AttachmentFile[]) => void;
  setSelectedAttachment: (attachment: AttachmentFile | null) => void;
  setCurrentAttachmentContext: (orderUid: string | null) => void;
  setAttachmentFlow: (flow: AttachmentFlow | null) => void;
  setAttachmentReadOnly: (readOnly: boolean) => void;
  setAttachmentToRemove: (uploadUid: string | null) => void;
  /**
   * Switches to another attachment context in one update, so the screens
   * subscribed to the context, the flow and the read-only flag re-render once
   * instead of three times. Called with no arguments this is the
   * nothing-is-open path (see `applyTop` in contextStack).
   */
  setAttachmentContext: (
    orderUid?: string | null,
    flow?: AttachmentFlow | null,
    readOnly?: boolean
  ) => void;
};

export const useAttachmentStore = create<AttachmentState & AttachmentActions>()(
  (set) => ({
    attachments: [],
    selectedAttachment: null,
    currentAttachmentContext: null,
    attachmentFlow: null,
    attachmentReadOnly: false,
    attachmentToRemove: null,

    setAttachments: (attachments) => set({ attachments }),
    setSelectedAttachment: (attachment) =>
      set({ selectedAttachment: attachment }),
    setCurrentAttachmentContext: (orderUid) =>
      set({ currentAttachmentContext: orderUid }),
    setAttachmentFlow: (flow) => set({ attachmentFlow: flow }),
    setAttachmentReadOnly: (readOnly) => set({ attachmentReadOnly: readOnly }),
    setAttachmentToRemove: (uploadUid) =>
      set({ attachmentToRemove: uploadUid }),
    setAttachmentContext: (orderUid = null, flow = null, readOnly = false) =>
      set({
        currentAttachmentContext: orderUid,
        attachmentFlow: flow,
        attachmentReadOnly: readOnly,
      }),
  })
);
